import Button from '../ui/Button'
import type { OnboardingAvailabilityDay } from '../../lib/providerOnboarding'
import { useLang } from '../../contexts/LangContext'

interface AvailabilityStepProps {
  availability: OnboardingAvailabilityDay[]
  saving: boolean
  onToggleDay: (dayOfWeek: number) => void
  onChangeTime: (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => void
  onBack: () => void
  onNext: () => void
}

const dayLabels = {
  el: ['Κυρ', 'Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ'],
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
} as const

export default function AvailabilityStep({
  availability,
  saving,
  onToggleDay,
  onChangeTime,
  onBack,
  onNext,
}: AvailabilityStepProps) {
  const { locale, t } = useLang()
  const labels = dayLabels[locale]

  return (
    <div className="space-y-8">
      <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
        <p className="text-sm leading-7 text-slate-600">
          {t(
            'Ρύθμισε ένα σταθερό εβδομαδιαίο ωράριο. Μπορείς αργότερα να το βελτιώσεις από το dashboard.',
            'Set a steady weekly schedule. You can refine it later from the dashboard.',
          )}
        </p>

        <div className="mt-6 space-y-3">
          {availability.map((day) => (
            <div
              key={day.dayOfWeek}
              className="flex flex-col gap-4 rounded-[1.25rem] border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between"
            >
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => onToggleDay(day.dayOfWeek)}
                  className={[
                    'relative inline-flex h-7 w-12 items-center rounded-full transition-colors',
                    day.active ? 'bg-primary-600' : 'bg-slate-300',
                  ].join(' ')}
                  aria-pressed={day.active}
                  aria-label={labels[day.dayOfWeek]}
                >
                  <span
                    className={[
                      'inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
                      day.active ? 'translate-x-6' : 'translate-x-1',
                    ].join(' ')}
                  />
                </button>

                <div>
                  <p className="text-base font-bold text-slate-950">{labels[day.dayOfWeek]}</p>
                  <p className="text-sm text-slate-500">
                    {day.active ? t('Διαθέσιμος', 'Available') : t('Κλειστό', 'Closed')}
                  </p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">
                  {t('Από', 'From')}
                  <input
                    type="time"
                    value={day.startTime}
                    disabled={!day.active}
                    onChange={(event) => onChangeTime(day.dayOfWeek, 'startTime', event.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 disabled:text-slate-400"
                  />
                </label>
                <label className="text-sm font-medium text-slate-700">
                  {t('Έως', 'To')}
                  <input
                    type="time"
                    value={day.endTime}
                    disabled={!day.active}
                    onChange={(event) => onChangeTime(day.dayOfWeek, 'endTime', event.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-100 disabled:text-slate-400"
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="secondary" size="lg" onClick={onBack}>
          {t('Πίσω', 'Back')}
        </Button>
        <Button type="button" size="lg" loading={saving} onClick={onNext}>
          {t('Αποθήκευση διαθεσιμότητας', 'Save availability')}
        </Button>
      </div>
    </div>
  )
}
